// core/data/users.ts — typed port of legacy src/scripts/data/users.js
// Display-string counts (e.g. '129K') are converted to integers here.
// Display formatting (e.g. 129000 → '129K') is a display-layer concern.

import type { User, SuggestedProfile } from '@/core/models';

// Mutable registry — populated at module init with static seed, extended at
// boot via hydrateAccountsIntoUsersRegistry and at runtime via registerUser.
const _registry: Record<string, User> = {
  'wayne-tech': {
    id: 'wayne-tech',
    likedPostIds: ['post-001', 'post-002', 'post-003', 'post-007', 'post-011'],
    likesPublic: false,
    name: 'WayneTech',
    handle: '@WayneTech',
    verified: true,
    premium: true,
    isAdmin: false,
    isSuperuser: false,
    avatarInitial: 'W',
    avatarKey: 'av-grad',
    banner: 'cyberpunk-x',
    bio: 'Tech enthusiast. Building tools like a true fanatic.',
    tags: ['#Developer', '#Innovation'],
    link: { label: 'waynetech.dev', href: 'https://waynetech.dev' },
    location: 'Gotham, US',
    joined: 'January 2024',
    role: 'Engineer / Founder',
    followerCount: 129000,
    stats: { following: 2500, followers: 129000, posts: 342, likes: 8400 },
    about: [
      { icon: 'briefcase', text: 'Engineer / Founder / Freedom Advocate' },
      { icon: 'globe',     text: 'Open Source Contributor' },
      { icon: 'calendar',  text: 'Joined January 2024' },
    ],
    aboutThisUser: [
      { icon: 'trophy', text: 'Founder of WayneTech' },
      { icon: 'mic',    text: 'Speaker @ DefCon, Open Source Summit' },
      { icon: 'globe',  text: 'Passionate about privacy, decentralization, and digital rights.' },
    ],
    topicPills: ['Open Source Collective', 'Privacy First'],
    mutuals: {
      count: 12,
      avatars: [
        { name: 'FreeThinker',   av: 'av-4' },
        { name: 'LibertyVoice',  av: 'av-5' },
        { name: 'OpenMind',      av: 'av-3' },
      ],
    },
    achievements: [
      { id: 'early-adopter', name: 'Early Adopter',  tone: 'gold'   },
      { id: 'top-creator',   name: 'Top 1% Creator', tone: 'blue'   },
      { id: 'verified-pro',  name: 'Verified Pro',   tone: 'violet' },
    ],
    statsOverview: {
      headline: [
        { label: 'Impressions', value: '2.4M' },
        { label: 'Engagement',  value: '89K'  },
        { label: 'Reach',       value: '124K' },
      ],
      spark: [12, 18, 15, 22, 28, 24, 33, 31, 38, 42, 47, 52],
    },
  },

  'freedom-seeker': {
    id: 'freedom-seeker',
    likedPostIds: ['post-004', 'post-009', 'post-013'],
    likesPublic: true,
    name: 'FreedomSeeker',
    handle: '@FreedomSeeker',
    verified: true,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-1',
    avatarInitial: 'F',
    bio: 'Free thinker. Defender of open dialogue.',
    role: 'Independent journalist',
    location: 'Portland, OR',
    link: { label: 'freedomseeker.news', href: '#' },
    followerCount: 24800,
    joined: 'March 2023',
    stats: { following: 892, followers: 24800, posts: 128, likes: 3200 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Independent journalist' },
      { icon: 'mic',    text: 'Host of the Open Dialogue podcast' },
      { icon: 'globe',  text: 'Covers free expression and digital rights.' },
    ],
    topicPills: ['Free Press', 'Open Dialogue'],
    mutuals: {
      count: 5,
      avatars: [
        { name: 'OpenMind',     av: 'av-3' },
        { name: 'LibertyVoice', av: 'av-5' },
      ],
    },
  },

  'liberty-mind': {
    id: 'liberty-mind',
    likedPostIds: ['post-001', 'post-008', 'post-012'],
    likesPublic: false,
    name: 'LibertyMind',
    handle: '@LibertyMind',
    verified: true,
    premium: true,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-2',
    avatarInitial: 'L',
    bio: 'Question everything. Build understanding.',
    role: 'Philosopher / Writer',
    location: 'Austin, TX',
    link: { label: 'libertymind.blog', href: '#' },
    followerCount: 18300,
    joined: 'July 2023',
    stats: { following: 640, followers: 18300, posts: 87, likes: 5100 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Author of "Think Freely"' },
      { icon: 'mic',    text: 'Speaker on civil liberties' },
      { icon: 'globe',  text: 'Writes about philosophy and open inquiry.' },
    ],
    topicPills: ['Civil Liberties', 'Philosophy'],
    mutuals: {
      count: 8,
      avatars: [
        { name: 'FreedomSeeker', av: 'av-1' },
        { name: 'OpenMind',      av: 'av-3' },
      ],
    },
  },

  'open-mind': {
    id: 'open-mind',
    likedPostIds: ['post-002', 'post-005'],
    likesPublic: false,
    name: 'OpenMind',
    handle: '@OpenMindX',
    verified: true,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-3',
    avatarInitial: 'O',
    bio: 'Conversations over conclusions.',
    role: 'Community builder',
    location: 'Denver, CO',
    link: { label: 'opendialogue.org', href: '#' },
    followerCount: 12600,
    joined: 'September 2023',
    stats: { following: 1100, followers: 12600, posts: 54, likes: 2400 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Founder of Open Dialogue Collective' },
      { icon: 'mic',    text: 'Community organizer' },
      { icon: 'globe',  text: 'Builds spaces for good-faith debate.' },
    ],
    topicPills: ['Community', 'Good-Faith Debate'],
    mutuals: {
      count: 6,
      avatars: [
        { name: 'FreedomSeeker', av: 'av-1' },
        { name: 'LibertyMind',   av: 'av-2' },
      ],
    },
  },

  'maya-patel': {
    id: 'maya-patel',
    likedPostIds: ['post-011', 'post-013'],
    likesPublic: false,
    name: 'Maya Patel',
    handle: '@mayap',
    verified: false,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-2',
    avatarInitial: 'M',
    bio: "Security researcher. Breaking things so you don't have to.",
    role: 'Security researcher',
    location: 'Seattle, WA',
    followerCount: 8400,
    joined: 'May 2023',
    stats: { following: 310, followers: 8400, posts: 96, likes: 1800 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Bug-bounty top 100' },
      { icon: 'globe',  text: 'Writes about appsec and privacy.' },
    ],
    topicPills: ['AppSec', 'Privacy'],
    mutuals: {
      count: 3,
      avatars: [{ name: 'WayneTech', av: 'av-grad' }],
    },
  },

  'jordan-lee': {
    id: 'jordan-lee',
    likedPostIds: ['post-009', 'post-005', 'post-001', 'post-007'],
    likesPublic: true,
    name: 'Jordan Lee',
    handle: '@jlee',
    verified: true,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-4',
    avatarInitial: 'J',
    bio: 'Open-source maintainer. Coffee-driven.',
    role: 'Maintainer',
    location: 'Toronto, CA',
    followerCount: 15100,
    joined: 'February 2023',
    stats: { following: 420, followers: 15100, posts: 211, likes: 6700 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Maintainer of several OSS libraries' },
      { icon: 'globe',  text: 'Believes in sustainable open source.' },
    ],
    topicPills: ['Open Source', 'Dev Tools'],
    mutuals: {
      count: 4,
      avatars: [
        { name: 'WayneTech',  av: 'av-grad' },
        { name: 'Maya Patel', av: 'av-2' },
      ],
    },
  },

  'sara-kim': {
    id: 'sara-kim',
    likedPostIds: ['post-007', 'post-012'],
    likesPublic: false,
    name: 'Sara Kim',
    handle: '@sarak',
    verified: false,
    premium: false,
    isAdmin: false,
    isSuperuser: false,
    avatarKey: 'av-5',
    avatarInitial: 'S',
    bio: 'Designer. Pixels with a conscience.',
    role: 'Product designer',
    location: 'Brooklyn, NY',
    followerCount: 6200,
    joined: 'November 2023',
    stats: { following: 530, followers: 6200, posts: 73, likes: 2000 },
    aboutThisUser: [
      { icon: 'trophy', text: 'Design lead, indie apps' },
      { icon: 'globe',  text: 'Writes about ethical product design.' },
    ],
    topicPills: ['Design', 'Ethics'],
    mutuals: {
      count: 2,
      avatars: [{ name: 'Jordan Lee', av: 'av-4' }],
    },
  },
};

// Suggested profiles for the Who-to-Follow right-rail.
// followerCount is an integer; display formatting lives in the UI layer.
export const suggestedProfiles: SuggestedProfile[] = [
  { id: 'mayap', name: 'Maya Patel', handle: '@mayap', av: 'av-2', verified: false, followerCount: 8_400 },
  { id: 'jlee', name: 'Jordan Lee', handle: '@jlee', av: 'av-4', verified: true, followerCount: 15_100 },
  { id: 'sarak', name: 'Sara Kim', handle: '@sarak', av: 'av-5', verified: false, followerCount: 6_200 },
];

/** Returns the live (mutable) registry record for the given id, or null. */
export function getUser(id: string): User | null {
  return _registry[id] ?? null;
}

/** Returns the live registry record matched by handle (case-insensitive, leading @ stripped). */
export function getUserByHandle(handle: string): User | null {
  const norm = handle.replace(/^@/, '').toLowerCase();
  return (
    Object.values(_registry).find(
      (u) => u.handle.replace(/^@/, '').toLowerCase() === norm,
    ) ?? null
  );
}

/** Adds or replaces a record in the registry (used at boot + sign-up). */
export function registerUser(record: User): void {
  if (!record?.id) return;
  _registry[record.id] = record;
}

/** Returns the full registry as a snapshot array (used by usersService). */
export function allUsers(): User[] {
  return Object.values(_registry);
}
